import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ShoppingBag, BookOpen, Video, PenTool, ArrowLeft, Mail, Heart, Tag, ExternalLink, Globe, GraduationCap } from 'lucide-react';

// --- 1. Analytics & SEO ---
const TRACKING_ID = "G-A4522832975"; 
const PIXEL_ID = "26584148491172994";

const AnalyticsHandler = () => {
  const location = useLocation();
  useEffect(() => {
    const currentPath = window.location.hash || window.location.pathname;
    if (window.gtag) { window.gtag('config', TRACKING_ID, { page_path: currentPath }); }
    if (window.fbq) { window.fbq('track', 'PageView'); }
    window.scrollTo(0, 0);
  }, [location]);
  return null;
};

const SEO = ({ title, description, path = "" }) => (
  <Helmet>
    <title>{title} | Lo3ta لقطة</title>
    <meta name="description" content={description} />
    <link rel="canonical" href={`https://lo3ta.com${path}`} />
  </Helmet>
);

const HeaderBack = () => {
  const navigate = useNavigate();
  const handleBack = () => {
    if (window.history.length > 2) { navigate(-1); }
    else { navigate('/'); }
  };
  return (
    <button onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#1e3a8a', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 'bold', padding: '8px 16px', borderRadius: '50px', marginBottom: '20px', fontSize: '13px' }}>
      <ArrowLeft size={14} /> العودة
    </button>
  );
};

const Card = ({ children, style }) => (
  <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '25px', border: '1px solid #f1f5f9', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', textAlign: 'center', ...style }}>
    {children}
  </div>
);

// --- 2. About & Privacy Components (Professional Content) ---
const AboutPage = () => (
  <div style={{ padding: '40px 5%', direction: 'rtl', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto' }}>
    <SEO title="من نحن" description="تعرف على منصة لقطة ورؤيتنا في تقديم أفضل العروض المختارة بعناية." />
    <HeaderBack />
    <Card style={{ textAlign: 'right', padding: '40px' }}>
      <h1 style={{ color: '#1e3a8a', borderBottom: '2px solid #f1f5f9', paddingBottom: '15px', marginBottom: '20px' }}>من نحن - منصة لقطة (Lo3ta)</h1>
      <p style={{ fontSize: '1.1rem' }}>
        مرحباً بكم في <strong>لوقطة (Lo3ta)</strong>، وجهتكم الأولى لاكتشاف أرقى المنتجات وأفضل العروض المختارة بعناية من منصات كبرى مثل نون وأمازون ويامورا. نحن نهدف لتبسيط عملية التسوق عبر ترشيح "اللقطات" الحقيقية التي توفر لكم المال والوقت.
      </p>
      <h3 style={{ color: '#be185d', marginTop: '30px' }}>رؤيتنا</h3>
      <p>تمكين المستهلك من الوصول إلى منتجات عالية الجودة بأسعار تنافسية مع ضمان تجربة تسوق آمنة وموثوقة عبر دعم مباشر وتوجيه صادق.</p>
      <h3 style={{ color: '#be185d', marginTop: '30px' }}>لماذا لقطة؟</h3>
      <ul style={{ paddingRight: '20px', listStyleType: 'disc', marginTop: '10px' }}>
        <li><strong>اختيارات خبيرة:</strong> نراجع جودة كل منتج قبل ترشيحه لضمان رضاكم.</li>
        <li><strong>شفافية تامة:</strong> نوفر الأسعار الحقيقية وكافة التفاصيل بوضوح دون تضليل.</li>
        <li><strong>دعم مباشر:</strong> نرافقكم عبر الواتساب لتأكيد طلباتكم وضمان سهولة الشحن.</li>
      </ul>
    </Card>
  </div>
);

const PrivacyPage = () => (
  <div style={{ padding: '40px 5%', direction: 'rtl', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto' }}>
    <SEO title="سياسة الخصوصية" description="نحن نلتزم بحماية بياناتك وخصوصيتك في منصة لقطة وفق المعايير الدولية." />
    <HeaderBack />
    <Card style={{ textAlign: 'right', padding: '40px' }}>
      <h1 style={{ color: '#1e3a8a', marginBottom: '20px' }}>سياسة الخصوصية</h1>
      <p>تلتزم منصة <strong>لوقطة</strong> بحماية خصوصية زوارها. توضح هذه السياسة كيفية التعامل مع معلوماتكم:</p>
      
      <h4 style={{ color: '#1e3a8a', marginTop: '20px' }}>1. جمع المعلومات</h4>
      <p>نجمع البيانات التي تقدمونها طواعية عند الطلب عبر الواتساب (مثل الاسم والعنوان) لإتمام عملية الشحن وتأكيد الطلب فقط.</p>
      
      <h4 style={{ color: '#1e3a8a', marginTop: '20px' }}>2. تكنولوجيا التتبع</h4>
      <p>نستخدم أدوات تحليلية مثل (Google Analytics) و (Meta Pixel) لفهم سلوك الزوار وتحسين أداء الموقع، ولا يتم استخدام هذه البيانات للتعرف على هويتكم الشخصية خارج إطار الموقع.</p>
      
      <h4 style={{ color: '#1e3a8a', marginTop: '20px' }}>3. روابط الطرف الثالث</h4>
      <p>يحتوي موقعنا على روابط لمواقع خارجية (أمازون، نون، إلخ). نحن غير مسؤولين عن سياسات الخصوصية الخاصة بهذه المواقع المستقلة.</p>
      
      <h4 style={{ color: '#1e3a8a', marginTop: '20px' }}>4. حقوقكم</h4>
      <p>يمكنكم دائماً طلب حذف بياناتكم أو الاستفسار عنها عبر البريد الإلكتروني: <strong>support@lo3ta.com</strong>.</p>
      
    </Card>
  </div>
);

// --- 3. Yamora Component ---
const YamoraDetails = ({ title, price, oldPrice, longDesc, images, deals }) => {
  const [activeImg, setActiveImg] = useState(images[0]);
  const [selectedDeal, setSelectedDeal] = useState(deals[0].name);
  const [loading, setLoading] = useState(false);
  
  // نظام التايمر التنازلي الاحترافي
  const [timeLeft, setTimeLeft] = useState({ minutes: 4, seconds: 45 });

  useEffect(() => {
    const timer = setInterval(() => {
      if (timeLeft.seconds > 0) {
        setTimeLeft(prev => ({ ...prev, seconds: prev.seconds - 1 }));
      } else if (timeLeft.minutes > 0) {
        setTimeLeft(prev => ({ minutes: prev.minutes - 1, seconds: 59 }));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const sendToWhatsApp = () => {
  setLoading(true);
  
  // التعديل هنا: غيرنا Lead لـ AddToCart وضفنا القيمة المالية
  if (window.fbq) { 
    window.fbq('track', 'AddToCart', { 
      content_name: title, 
      content_category: 'Yamora Fashion',
      value: parseFloat(price.replace(/[^\d.]/g, '')), // بيحول "675 ج.م" لرقم 675
      currency: 'EGP'
    }); 
  }
  
  setTimeout(() => {
    const fullImgPath = `${window.location.origin}${activeImg}`;
    const message = `🛍️ *طلب جديد من Lo3ta*%0A--------------------------%0A📦 *المنتج:* ${title}%0A🏷️ *العرض:* ${selectedDeal}%0A🖼️ *الموديل:* ${fullImgPath}%0A💰 *السعر:* ${price}%0A--------------------------%0A*بيانات الشحن:*%0A👤 *الاسم:* %0A📞 *الموبايل:* %0A🏠 *العنوان:* %0A--------------------------%0A🚚 *شحن مجاني ومعاينة عند الاستلام*`;
    window.location.href = `https://wa.me/201147095615?text=${message}`;
    setLoading(false);
  }, 800); 
};

  return (
    <div style={{ direction: 'rtl', padding: '0 0 120px 0', maxWidth: '1000px', margin: '0 auto' }}>
      <SEO title={title} description={longDesc} />
      
      {/* 1. شريط الخصم الوردي العلوي */}
      <div style={{ background: '#be185d', color: '#fff', textAlign: 'center', padding: '12px', fontWeight: 'bold', fontSize: '15px', marginBottom: '20px' }}>
        🔥 الحق العرض! خصم لفترة محدودة + شحن مجاني
      </div>

      <div style={{ padding: '0 5%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
           <HeaderBack />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
          {/* قسم الصور */}
          <div>
            <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
              <img src={activeImg} style={{ width: '100%', display: 'block' }} alt={title} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginTop: '10px' }}>
              {images.map((img, i) => (
                <img key={i} src={img} onClick={() => setActiveImg(img)} style={{ width: '100%', height: '70px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: activeImg === img ? '3px solid #be185d' : '1px solid #eee' }} />
              ))}
            </div>
          </div>

          {/* قسم التفاصيل */}
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '900', color: '#1e293b', marginBottom: '10px' }}>{title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
              <span style={{ fontSize: '2.2rem', fontWeight: 'bold', color: '#be185d' }}>{price}</span>
              <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '1.2rem' }}>{oldPrice}</span>
            </div>

            {/* 2. عداد الوقت (التايمر) */}
            <div style={{ background: '#fffbe6', border: '1px dashed #f59e0b', padding: '12px', borderRadius: '12px', marginBottom: '15px', textAlign: 'center', color: '#b45309', fontWeight: 'bold', fontSize: '1.1rem' }}>
              ⏳ ينتهي عرض الشحن المجاني خلال: {timeLeft.minutes}:{timeLeft.seconds < 10 ? `0${timeLeft.seconds}` : timeLeft.seconds}
            </div>

            {/* 3. جملة المعاينة قبل الدفع */}
            <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '12px', marginBottom: '20px', color: '#166534', fontSize: '0.95rem', border: '1px solid #bbf7d0', fontWeight: 'bold' }}>
               🛡️ حقك تفتحي الشحنة وتعايني الخامة قبل الدفع<br/>
               🚚 الشحن مجاني تماماً لفترة محدودة
            </div>

            <p style={{ lineHeight: '1.8', color: '#475569', background: '#f8fafc', padding: '20px', borderRadius: '16px', fontSize: '1rem', whiteSpace: 'pre-line' }}>{longDesc}</p>

            {/* قسم اختيار العروض */}
            <div style={{ marginTop: '25px', border: '2px solid #be185d', padding: '25px', borderRadius: '20px', background: '#fff' }}>
              <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#be185d', fontSize: '1.2rem' }}>🎁 اختاري العرض المناسب</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                {deals.map((d, i) => (
                  <div key={i} onClick={() => setSelectedDeal(d.name)} style={{ padding: '15px', borderRadius: '12px', border: selectedDeal === d.name ? '2px solid #be185d' : '1px solid #e2e8f0', textAlign: 'center', cursor: 'pointer', background: selectedDeal === d.name ? '#fdf2f8' : '#fff', fontWeight: 'bold', transition: '0.3s' }}>
                    {d.name}<br/><span style={{fontSize: '1.1rem'}}>{d.price}</span>
                  </div>
                ))}
              </div>
              
              <button onClick={sendToWhatsApp} style={{ width: '100%', padding: '20px', background: '#25D366', color: '#fff', border: 'none', borderRadius: '15px', fontWeight: 'bold', fontSize: '1.3rem', cursor: 'pointer', boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)' }}>
                  {loading ? "جاري التحويل..." : "اطلبي الآن عبر واتساب 💬"}
              </button>
              
              <p style={{ textAlign: 'center', fontSize: '13px', color: '#64748b', marginTop: '12px', fontWeight: 'bold' }}>
                * سيتم فتح واتساب لتأكيد الاسم والعنوان
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* الزر العايم (Sticky WhatsApp) */}
      <div onClick={sendToWhatsApp} style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '90%', maxWidth: '450px', background: '#25D366', color: '#fff', textAlign: 'center', padding: '18px', borderRadius: '50px', fontWeight: '900', zIndex: 9999, cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', fontSize: '1.2rem' }}>الحق العرض واطلبي واتساب 💬</div>
    </div>
  );
};

// --- 4. Partner Details Component ---
const PartnerDetails = ({ title, icon: Icon, buyUrl, desc, promoCode, color }) => {
  const handlePartnerClick = () => {
    if (window.fbq) { 
      window.fbq('track', 'AddToCart', { 
        content_name: title, 
        content_category: 'Affiliate Marketing', // تصنيف عام للشركاء
        // ملحوظة: شلنا الـ price هنا لأنه مش موجود في الـ props عشان الكود ميعملش Crash
      }); 
    }
    
    if (window.gtag) { 
      window.gtag('event', 'select_item', { 'item_name': title }); 
    }

    // تأخير بسيط للتأكد من إرسال البيانات قبل فتح الرابط الجديد
    setTimeout(() => { 
      window.open(buyUrl, '_blank'); 
    }, 500);
  };

  return (
    <div style={{ padding: '20px 5%', maxWidth: '600px', margin: '0 auto', direction: 'rtl' }}>
      <SEO title={title} description={desc} />
      <HeaderBack />
      <Card>
        <div style={{ color: color, marginBottom: '15px' }}><Icon size={50} /></div>
        <h2 style={{ fontSize: '1.6rem' }}>{title}</h2>
        <p style={{ color: '#475569', margin: '20px 0' }}>{desc}</p>
        {promoCode && (
          <div style={{ background: '#fffbe6', border: '2px dashed #feee00', padding: '15px', borderRadius: '12px', marginBottom: '25px' }}>
            <div style={{ fontSize: '26px', fontWeight: '900' }}>{promoCode}</div>
          </div>
        )}
        <button onClick={handlePartnerClick} style={{ width: '100%', padding: '15px', background: color || '#1e3a8a', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }}>
          زيارة الموقع الآن <ExternalLink size={18} style={{verticalAlign:'middle'}}/>
        </button>
      </Card>
    </div>
  );
};

const btnAction = (bg, width='100%', color='white') => ({ width, padding: '12px', borderRadius: '12px', border: 'none', backgroundColor: bg, color, fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' });

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <AnalyticsHandler />
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          flexDirection: 'column', 
          backgroundColor: '#fcfcfc', 
          fontFamily: 'system-ui, sans-serif' 
        }}>
          
          <nav style={{ padding: '10px 5%', background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <img src="/images/logo3d.png" style={{ width: '35px' }} alt="Logo" />
              <span style={{ fontSize: '22px', fontWeight: '950', color: '#1e3a8a' }}>Lo3ta</span>
            </Link>
          </nav>

          {/* التعديل الجوهري: flex: 1 بيخلي الـ main يملأ الفراغ ويجبر الفوتر ينزل تحت */}
          <main style={{ flex: 1, paddingBottom: '40px' }}>
            <Routes>
              <Route path="/" element={
                <div style={{ padding: '30px 5%', textAlign: 'center' }}>
                  <h1 style={{ fontSize: '3rem', fontWeight: '950' }}>Lo3ta</h1>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginTop: '30px' }}>
                    <Card><Heart color="#be185d" size={35}/><h4>Yamora Fashion</h4><Link to="/yamora"><button style={btnAction('#be185d')}>استكشف</button></Link></Card>
                    <Card><ShoppingBag color="#feee00" size={35}/><h4>Noon Shopping</h4><Link to="/noon"><button style={btnAction('#1e3a8a')}>استكشف</button></Link></Card>
                    <Card><Globe color="#ff9900" size={35}/><h4>Amazon Global</h4><Link to="/amazon"><button style={btnAction('#1e3a8a')}>استكشف</button></Link></Card>
                    <Card><GraduationCap color="#0056d2" size={35}/><h4>Coursera</h4><Link to="/coursera"><button style={btnAction('#1e3a8a')}>استكشف</button></Link></Card>
                    <Card><BookOpen color="#a435f0" size={35}/><h4>Udemy</h4><Link to="/udemy"><button style={btnAction('#1e3a8a')}>استكشف</button></Link></Card>
                    <Card><PenTool color="#8a2be2" size={35}/><h4>Jasper AI</h4><Link to="/jasper"><button style={btnAction('#1e3a8a')}>استكشف</button></Link></Card>
                    <Card><Video color="#00d1ff" size={35}/><h4>InVideo AI</h4><Link to="/invideo"><button style={btnAction('#1e3a8a')}>استكشف</button></Link></Card>
                  </div>
                </div>
              } />
              
              <Route path="/yamora" element={
                <div style={{ padding: '30px 5%', direction: 'rtl' }}>
                  <HeaderBack />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                    <Card style={{padding:0, overflow:'hidden'}}><img src="/images/es4.jpg" style={{width:'100%', height:'280px', objectFit:'cover'}} alt="Luxury" /><h3>عباية الفخامة (ستان أرماني)</h3><Link to="/yamora/luxury"><button style={btnAction('#be185d', '90%')}>عرض التفاصيل</button></Link></Card>
                    <Card style={{padding:0, overflow:'hidden'}}><img src="/images/es6.jpg" style={{width:'100%', height:'280px', objectFit:'cover'}} alt="Velvet" /><h3>إسدال القطيفة الملكي</h3><Link to="/yamora/velvet"><button style={btnAction('#be185d', '90%')}>عرض التفاصيل</button></Link></Card>
                  </div>
                </div>
              } />

              <Route path="/yamora/luxury" element={<YamoraDetails title="عباية الفخامة (ستان أرماني) ✨" price="895 ج.م" images={["/images/es.jpg", "/images/es3.jpg", "/images/es5.jpg", "/images/Esdal1.jpg", "/images/es4.jpg"]} longDesc={`✨ الدلع والشياكة في طقم واحد!\nنعومة تخطت الحدود وتصميم يخليكي برنسيسة في بيتك.\n✅ خامة ستان حرير أرماني مستوردة.\n✅ تقفيل راقي جداً يليق بذوقك.`} deals={[{name:"قطعة واحدة", price:"895ج"}, {name:"قطعتين (خصم)", price:"1700ج"}, {name:"3 قطع (توفير سوبر)", price:"2400ج"}]} />} />
              <Route path="/yamora/velvet" element={<YamoraDetails title="إسدال القطيفة الملكي ❄️" price="675 ج.م" images={["/images/es7.jpg", "/images/es8.jpg", "/images/es9.jpg", "/images/es10.jpg", "/images/es6.jpg"]} longDesc={`❄️ الشياكة اللي بتدفي!\nاستقبلي ضيوفك بطلّة ملكية وادفي في البرد ده بأشيك قطيفة مستوردة.\n✅ خامة ثقيلة ودافئة جداً.\n✅ طرحة منفصلة وشكل يبهر أي حد يشوفك.`} deals={[{name:"قطعة واحدة", price:"675ج"}, {name:"قطعتين (توفير)", price:"1250ج"}, {name:"3 قطع (خصم سوبر)", price:"1850ج"}]} />} />
              
              <Route path="/noon" element={
                <div style={{ padding: '30px 5%', direction: 'rtl' }}>
                  <HeaderBack />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                    <Card style={{padding:0, overflow:'hidden'}}><img src="/images/fryer1.jpg" style={{width:'100%', height:'220px', objectFit:'cover'}} alt="Fryer" /><h4>Air Fryer</h4><Link to="/noon/air-fryer"><button style={btnAction('#feee00', '90%', '#1e293b')}>عرض التفاصيل</button></Link></Card>
                    <Card style={{padding:0, overflow:'hidden'}}><img src="/images/vacuum1.jpg" style={{width:'100%', height:'220px', objectFit:'cover'}} alt="Vacuum" /><h4>مكنسة لاسلكية</h4><Link to="/noon/vacuum"><button style={btnAction('#feee00', '90%', '#1e293b')}>عرض التفاصيل</button></Link></Card>
                  </div>
                </div>
              } />

              <Route path="/noon/air-fryer" element={<PartnerDetails title="Air Fryer 5.6L" icon={ShoppingBag} buyUrl="https://www.noon.com/egypt-ar/p-12345" promoCode="LO3TA" desc="أقوى عروض نون مصر على القلايات الهوائية بخصم إضافي." color="#feee00" />} />
              <Route path="/noon/vacuum" element={<PartnerDetails title="Cordless Vacuum" icon={ShoppingBag} buyUrl="https://www.noon.com/egypt-ar/p-67890" promoCode="LO3TA" desc="مكنسة لاسلكية عملية وقوية لتنظيف أسهل بخصم حصري." color="#feee00" />} />
              <Route path="/amazon" element={<PartnerDetails title="Amazon Store" icon={Globe} buyUrl="https://amazon.com" desc="تسوق من أمازون العالمي بأسعار تنافسية." color="#ff9900" />} />
              <Route path="/coursera" element={<PartnerDetails title="Coursera" icon={GraduationCap} buyUrl="https://coursera.org" desc="شهادات معتمدة من كبرى الجامعات العالمية." color="#0056d2" />} />
              <Route path="/udemy" element={<PartnerDetails title="Udemy" icon={BookOpen} buyUrl="https://udemy.com" desc="أكبر منصة للكورسات في كافة المجالات." color="#a435f0" />} />
              <Route path="/jasper" element={<PartnerDetails title="Jasper AI" icon={PenTool} buyUrl="https://jasper.ai" desc="مساعدك الذكي لكتابة المحتوى التسويقي." color="#8a2be2" />} />
              <Route path="/invideo" element={<PartnerDetails title="InVideo AI" icon={Video} buyUrl="https://invideo.io" desc="حول نصوصك إلى فيديوهات مذهلة." color="#00d1ff" />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>

          <footer style={{ background: '#0f172a', color: '#fff', padding: '30px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '15px' }}>
              <Link to="/about" style={{ color: '#fff', textDecoration: 'none' }}>من نحن</Link>
              <Link to="/privacy" style={{ color: '#fff', textDecoration: 'none' }}>سياسة الخصوصية</Link>
            </div>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '10px' }}>
              <Mail size={14} style={{ verticalAlign: 'middle', marginLeft: '5px' }} /> 
              اتصل بنا: <a href="mailto:support@lo3ta.com" style={{ color: '#fff', textDecoration: 'none' }}>support@lo3ta.com</a>
            </p>
            <div style={{ color: '#64748b', fontSize: '12px', borderTop: '1px solid #1e293b', paddingTop: '15px' }}>
              <p>© 2026 Lo3ta.com</p>
            </div>
          </footer>
          
        </div>
      </Router>
    </HelmetProvider>
  );
}